import api from './api';

export interface UnifiedFunction {
  id: string;
  name: string;
  display_name: string | null;
  description: string | null;
  is_tool: boolean;
  is_eval_function: boolean;
  supports_canned_data: boolean;
  is_active: boolean;
  is_builtin: boolean;
  usage_count: number;
  created_at: string | null;
}

export interface UnifiedFunctionDetail extends UnifiedFunction {
  code: string | null;
  tool_schema: Record<string, unknown> | null;
  tool_returns_schema: Record<string, unknown> | null;
  returns_ground_truth: boolean;
  timeout_ms: number;
  capabilities: Record<string, unknown>;
  config_schema: Record<string, unknown> | null;  // JSON Schema for eval function config
  updated_at: string | null;
}

export interface CreateFunctionRequest {
  name: string;
  code: string;
  is_tool?: boolean;
  is_eval_function?: boolean;
  display_name?: string;
  description?: string;
  tool_schema?: Record<string, unknown>;
  tool_returns_schema?: Record<string, unknown>;
  returns_ground_truth?: boolean;
  timeout_ms?: number;
  supports_canned_data?: boolean;
  capabilities?: Record<string, unknown>;
  config_schema?: Record<string, unknown>;  // JSON Schema for eval function config
}

export interface UpdateFunctionRequest {
  name?: string;
  code?: string;
  is_tool?: boolean;
  is_eval_function?: boolean;
  display_name?: string;
  description?: string;
  tool_schema?: Record<string, unknown>;
  tool_returns_schema?: Record<string, unknown>;
  returns_ground_truth?: boolean;
  timeout_ms?: number;
  supports_canned_data?: boolean;
  capabilities?: Record<string, unknown>;
  config_schema?: Record<string, unknown>;  // JSON Schema for eval function config
  is_active?: boolean;
}

export interface TestFunctionRequest {
  params: Record<string, unknown>;
  mode?: 'tool' | 'eval';
  eval_context?: Record<string, unknown>;
}

export interface TestFunctionResponse {
  success: boolean;
  result: Record<string, unknown> | null;
  error: string | null;
  execution_time_ms: number;
}

export interface ValidateFunctionResponse {
  valid: boolean;
  errors: string[];
}

export interface CannedDataMapping {
  id: string;
  input_pattern: Record<string, unknown>;
  output_data: Record<string, unknown>;
  match_strategy: 'exact' | 'partial' | 'fuzzy' | 'regex';
  description: string | null;
  priority: number;
  created_at: string;
}

export interface CreateCannedDataMappingRequest {
  input_pattern: Record<string, unknown>;
  output_data: Record<string, unknown>;
  match_strategy?: 'exact' | 'partial' | 'fuzzy' | 'regex';
  description?: string;
  priority?: number;
}

const API_BASE = '/unified-functions';

export const unifiedFunctionsApi = {
  // CRUD
  list: async (filters?: {
    is_tool?: boolean;
    is_eval_function?: boolean;
    include_inactive?: boolean;
  }): Promise<UnifiedFunction[]> => {
    const response = await api.get<UnifiedFunction[]>(API_BASE, { params: filters });
    return response.data;
  },

  get: async (id: string): Promise<UnifiedFunctionDetail> => {
    const response = await api.get<UnifiedFunctionDetail>(`${API_BASE}/${id}`);
    return response.data;
  },

  create: async (data: CreateFunctionRequest): Promise<UnifiedFunctionDetail> => {
    const response = await api.post<UnifiedFunctionDetail>(API_BASE, data);
    return response.data;
  },

  update: async (id: string, data: UpdateFunctionRequest): Promise<UnifiedFunctionDetail> => {
    const response = await api.put<UnifiedFunctionDetail>(`${API_BASE}/${id}`, data);
    return response.data;
  },

  delete: async (id: string, softDelete = true): Promise<void> => {
    await api.delete(`${API_BASE}/${id}`, { params: { soft_delete: softDelete } });
  },

  // Validation & Testing
  validate: async (id: string): Promise<ValidateFunctionResponse> => {
    const response = await api.post<ValidateFunctionResponse>(`${API_BASE}/${id}/validate`);
    return response.data;
  },

  test: async (id: string, request: TestFunctionRequest): Promise<TestFunctionResponse> => {
    const response = await api.post<TestFunctionResponse>(`${API_BASE}/${id}/test`, request);
    return response.data;
  },

  // Canned Data
  listCannedData: async (functionId: string): Promise<CannedDataMapping[]> => {
    const response = await api.get<CannedDataMapping[]>(`${API_BASE}/${functionId}/canned-data`);
    return response.data;
  },

  createCannedData: async (
    functionId: string,
    data: CreateCannedDataMappingRequest
  ): Promise<CannedDataMapping> => {
    const response = await api.post<CannedDataMapping>(
      `${API_BASE}/${functionId}/canned-data`,
      data
    );
    return response.data;
  },

  updateCannedData: async (
    functionId: string,
    mappingId: string,
    data: CreateCannedDataMappingRequest
  ): Promise<CannedDataMapping> => {
    const response = await api.put<CannedDataMapping>(
      `${API_BASE}/${functionId}/canned-data/${mappingId}`,
      data
    );
    return response.data;
  },

  deleteCannedData: async (functionId: string, mappingId: string): Promise<void> => {
    await api.delete(`${API_BASE}/${functionId}/canned-data/${mappingId}`);
  },

  testCannedData: async (
    functionId: string,
    params: Record<string, unknown>
  ): Promise<{ matched: boolean; output: Record<string, unknown> | null }> => {
    const response = await api.post(`${API_BASE}/${functionId}/test-canned`, params);
    return response.data;
  },
};

export default unifiedFunctionsApi;
